import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { API, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  FloppyDisk, FilePdf, Plus, TrashSimple, ArrowLeft, Envelope, Package, Truck, Users, FileText,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import ItemsImportDialog from "@/components/ItemsImportDialog";
import SupplierAutocomplete from "@/components/SupplierAutocomplete";
import SupplierQuickCreate from "@/components/SupplierQuickCreate";
import { Upload } from "@phosphor-icons/react";

const UNITS = ["adet", "kg", "ton", "m", "m²", "m³", "lt", "torba", "paket", "palet", "kutu", "koli", "rulo"];
const CHANNELS = ["WhatsApp", "E-posta", "Telefon", "Yüz Yüze", "Diğer"];
const RESPONSES = [
  { value: "beklemede", label: "Beklemede" },
  { value: "verildi", label: "Verildi" },
  { value: "verilmedi", label: "Verilmedi" },
];

const emptyForm = {
  code: "",
  request_date: new Date().toISOString().slice(0, 10),
  site_id: "", site_name: "",
  delivery_address: "",
  delivery_date_text: "Program dahilinde gerçekleştirilecektir.",
  contact_person_name: "",
  contact_person_phone: "",
  shipping_note: "Lütfen sunduğunuz fiyat teklifinde belirtiniz. Aksi belirtilmedikçe nakliye dahil kabul edilecektir.",
  payment_type_note: "Lütfen sunduğunuz fiyat teklifinde ödeme koşullarını belirtiniz.",
  payment_terms_note: "Lütfen sunduğunuz fiyat teklifinde vade koşullarını belirtiniz.",
  additional_notes: "İlave maliyet kalemlerini (palet, strecth, indirme, yükleme vb.) fiyat teklifinde ayrı bir kalemde belirtiniz. Sipariş verilecek malzeme miktarlarında artış veya azalış meydana gelebilir.",
  requester_name: "",
  status: "acik",
  items: [],
  recipients: [],
};

const Section = ({ icon: Icon, title, subtitle, children, actions }) => (
  <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-[#1B4776] flex items-center justify-center">
          <Icon size={16} weight="duotone" className="text-[#95C11F]" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-900 font-heading">{title}</div>
          {subtitle && <div className="text-[10px] uppercase tracking-widest text-slate-500">{subtitle}</div>}
        </div>
      </div>
      {actions}
    </div>
    {children}
  </Card>
);

export default function RFQForm() {
  const nav = useNavigate();
  const params = useParams();
  const isNew = params.id === "yeni" || !params.id;
  const [searchParams] = useSearchParams();
  const siteParam = isNew ? (searchParams.get("site") || "") : "";
  const siteLocked = !!siteParam;
  const askConfirm = useConfirm();

  const [form, setForm] = useState(emptyForm);
  const [suppliers, setSuppliers] = useState([]);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedItemIdx, setSelectedItemIdx] = useState(new Set());

  // "Yeni Firma" sheet — hangi recipient satırından açıldı?
  const [supplierSheetOpen, setSupplierSheetOpen] = useState(false);
  const [supplierSheetForRecipient, setSupplierSheetForRecipient] = useState(null);
  const [supplierSheetPending, setSupplierSheetPending] = useState({});
  const [supplierSaving, setSupplierSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImport = (newItems) => {
    // Boş/varsayılan tek satırı ezmeyi tercih et
    setForm(f => {
      const existing = (f.items || []).filter(it => (it.material_name || "").trim() || parseFloat(it.quantity) > 0);
      const combined = [
        ...existing,
        ...newItems.map(it => ({
          material_name: it.material_name || "",
          unit: it.unit || "adet",
          quantity: it.quantity,
        })),
      ];
      return { ...f, items: combined };
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const [sup, si] = await Promise.all([
          axios.get(`${API}/suppliers`),
          axios.get(`${API}/sites`),
        ]);
        setSuppliers(sup.data);
        setSites(si.data);
        if (isNew) {
          const lockSid = siteParam && siteParam !== "merkez" ? siteParam : "";
          const { data } = await axios.get(`${API}/rfqs/next-code`, { params: lockSid ? { site_id: lockSid } : {} });
          const ls = si.data.find(x => x.id === lockSid);
          setForm(f => ({ ...f, code: data.code, ...(ls ? { site_id: ls.id, site_name: ls.name || "" } : {}) }));
        } else {
          const { data } = await axios.get(`${API}/rfqs/${params.id}`);
          setForm({ ...emptyForm, ...data, items: data.items || [], recipients: data.recipients || [] });
        }
        setLoaded(true);
      } catch (e) { toast.error(formatErr(e)); }
    })();
    // eslint-disable-next-line
  }, [params.id]);

  // Items
  const addItem = () => set("items", [...form.items, { material_name: "", unit: "adet", quantity: "" }]);
  const updateItem = (idx, patch) =>
    set("items", form.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = async (idx) => {
    const ok = await askConfirm({
      title: "Kalemi Sil",
      description: "Bu malzeme kalemini silmek istiyor musunuz?",
      destructive: true, confirmText: "Sil",
    });
    if (!ok) return;
    set("items", form.items.filter((_, i) => i !== idx));
    setSelectedItemIdx(new Set());
  };

  const toggleItemSel = (idx) => {
    setSelectedItemIdx(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const clearItemSel = () => setSelectedItemIdx(new Set());
  const toggleAllItems = () => {
    if (selectedItemIdx.size === form.items.length) setSelectedItemIdx(new Set());
    else setSelectedItemIdx(new Set(form.items.map((_, i) => i)));
  };
  const bulkRemoveItems = async () => {
    if (selectedItemIdx.size === 0) return;
    const ok = await askConfirm({
      title: "Kalemleri Sil",
      description: `${selectedItemIdx.size} kalemi silmek istiyor musunuz? Bu işlem geri alınamaz.`,
      destructive: true, confirmText: "Sil",
    });
    if (!ok) return;
    set("items", form.items.filter((_, i) => !selectedItemIdx.has(i)));
    setSelectedItemIdx(new Set());
  };

  // Recipients
  const addRecipient = () => set("recipients", [
    ...form.recipients,
    { supplier_id: "", supplier_name: "", contact_person: "", channel: "WhatsApp", sent_date: form.request_date, response: "beklemede", note: "" },
  ]);
  const updateRecipient = (idx, patch) =>
    set("recipients", form.recipients.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeRecipient = async (idx) => {
    const ok = await askConfirm({
      title: "Firmayı Kaldır",
      description: "Bu firmayı talep listesinden çıkarmak istiyor musunuz?",
      destructive: true, confirmText: "Kaldır",
    });
    if (!ok) return;
    set("recipients", form.recipients.filter((_, i) => i !== idx));
  };
  const pickSupplierForRecipient = (idx, supplierId) => {
    if (!supplierId) {
      updateRecipient(idx, { supplier_id: "", supplier_name: "" });
      return;
    }
    const s = suppliers.find(x => x.id === supplierId);
    if (s) updateRecipient(idx, { supplier_id: s.id, supplier_name: s.name });
  };

  // "Yeni Firma" panelini aç — hangi recipient satırı için, önceden yazılmış isim varsa preset et
  const openSupplierSheetForRecipient = (idx) => {
    const r = form.recipients[idx] || {};
    setSupplierSheetForRecipient(idx);
    // Serbest yazılmış isim varsa ticari unvana preset (SupplierQuickCreate title'dan short-name türetir)
    setSupplierSheetPending(r.supplier_name ? { title: r.supplier_name } : {});
    setSupplierSheetOpen(true);
  };

  // Recipient satırında SupplierQuickCreate ile yeni firma kaydı
  const saveSupplierFromRecipient = async (payload) => {
    setSupplierSaving(true);
    try {
      const r = await axios.post(`${API}/suppliers`, payload);
      const created = r.data;
      setSuppliers(prev => {
        const exists = prev.find(s => s.id === created.id);
        return exists ? prev : [...prev, created].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", "tr"));
      });
      // Açık olan recipient satırına yeni firmayı ata
      if (supplierSheetForRecipient !== null) {
        updateRecipient(supplierSheetForRecipient, {
          supplier_id: created.id,
          supplier_name: created.name || "",
        });
      }
      toast.success(`"${created.name}" firmalar listesine eklendi ve seçildi`);
      setSupplierSheetOpen(false);
      setSupplierSheetForRecipient(null);
      return created;
    } catch (e) {
      toast.error(formatErr(e));
      return null;
    } finally { setSupplierSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Revizyon zinciri alanları
        revision: parseInt(form.revision || 1) || 1,
        parent_rfq_id: form.parent_rfq_id || "",
        parent_rfq_code: form.parent_rfq_code || "",
        items: form.items.map(it => ({
          material_name: (it.material_name || "").trim(),
          unit: it.unit || "adet",
          quantity: parseFloat(it.quantity) || 0,
        })).filter(it => it.material_name),
        recipients: (form.recipients || []).map(r => ({
          ...r,
          supplier_id: r.supplier_id || "",
          supplier_name: (r.supplier_name || "").trim(),
          contact_person: (r.contact_person || "").trim(),
          channel: r.channel || "WhatsApp",
          sent_date: r.sent_date || "",
          response: r.response || "beklemede",
          note: r.note || "",
        })).filter(r => r.supplier_name),
      };
      let saved;
      if (isNew) {
        ({ data: saved } = await axios.post(`${API}/rfqs`, payload));
      } else {
        ({ data: saved } = await axios.put(`${API}/rfqs/${params.id}`, payload));
      }
      toast.success(isNew ? "Talep oluşturuldu" : "Güncellendi");
      nav(backTo);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const openPdf = () => {
    if (isNew) { toast.error("Önce kaydedin"); return; }
    window.open(`${API}/rfqs/${params.id}/pdf`, "_blank", "noopener,noreferrer");
  };

  const backTo = `/teklif-talepleri/santiye/${form.site_id || siteParam || "merkez"}`;

  if (!loaded) {
    return <Shell title="Teklif Talebi" subtitle="Yükleniyor..." hideSiteSelector />;
  }

  return (
    <Shell
      title={isNew ? "Yeni Teklif Talebi" : `Teklif Talebi: ${form.code}${(form.revision || 1) > 1 ? ` (Rev.${String(form.revision).padStart(2, "0")})` : ""}`}
      subtitle={form.parent_rfq_code ? `Satın Alma Yönetimi · Kaynak: ${form.parent_rfq_code}` : "Satın Alma Yönetimi"}
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => nav(backTo)} data-testid="rfq-form-back" className="rounded-none sm:rounded-sm">
            <ArrowLeft size={14} className="mr-1.5" /> Liste
          </Button>
          {!isNew && (
            <Button variant="outline" data-testid="rfq-form-goto-offers"
                    onClick={() => nav(`/gelen-teklifler/${form.site_id || "merkez"}?rfq=${params.id}&code=${encodeURIComponent(form.code || "")}`)}
                    className="rounded-none sm:rounded-sm border-[#1B4776]/30 text-[#1B4776]">
              <Envelope size={14} className="mr-1.5" /> Gelen Teklifler
            </Button>
          )}
          {!isNew && (
            <Button variant="outline" onClick={openPdf} data-testid="rfq-download-pdf" className="rounded-none sm:rounded-sm border-red-200 text-red-700 hover:bg-red-50">
              <FilePdf size={14} className="mr-1.5" /> PDF
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="rfq-save" className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
            <FloppyDisk size={14} className="mr-1.5" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      }
    >
      {/* Talep Bilgileri */}
      <Section icon={FileText} title="Talep Bilgileri" subtitle="Talep no + tarih + durum">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Talep No.</Label>
            <Input value={form.code} onChange={e => set("code", e.target.value)}
                   placeholder="RFQ-MSS-MRK-001" data-testid="rfq-code"
                   className="mt-2 rounded-none sm:rounded-sm font-mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Talep Tarihi</Label>
            <Input type="date" value={form.request_date} onChange={e => set("request_date", e.target.value)}
                   data-testid="rfq-date"
                   className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şantiye (Ops.)</Label>
            <Select value={form.site_id || "none"} disabled={siteLocked} onValueChange={async (v) => {
              if (v === "none") {
                setForm(f => ({ ...f, site_id: "", site_name: "" }));
                if (isNew) {
                  try {
                    const { data } = await axios.get(`${API}/rfqs/next-code`);
                    setForm(f => ({ ...f, code: data.code }));
                  } catch (e) {}
                }
                return;
              }
              const s = sites.find(x => x.id === v);
              setForm(f => ({ ...f, site_id: v, site_name: s?.name || "" }));
              if (isNew) {
                try {
                  const { data } = await axios.get(`${API}/rfqs/next-code`, { params: { site_id: v } });
                  setForm(f => ({ ...f, code: data.code }));
                } catch (e) {}
              }
            }}>
              <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="rfq-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Genel (proje yok)</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Durum</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="rfq-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="acik">Açık</SelectItem>
                <SelectItem value="kapali">Kapalı</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      {/* Malzeme Kalemleri */}
      <Section
        icon={Package}
        title="Malzeme Kalemleri"
        subtitle={`${form.items.length} kalem`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}
                    data-testid="rfq-import-items"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold">
              <Upload size={14} className="mr-1.5" /> İçe Aktar
            </Button>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="rfq-add-item" className="rounded-none sm:rounded-sm">
              <Plus size={14} className="mr-1.5" /> Kalem Ekle
            </Button>
          </div>
        }
      >
        {form.items.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-300">
            Henüz kalem yok. "Kalem Ekle" ile malzeme satırı ekleyin.
          </div>
        ) : (
          <div>
            {/* Toplu Seçim Bar'ı */}
            {selectedItemIdx.size > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-3 p-2.5 bg-[#1B4776] text-white rounded-none sm:rounded-sm"
                   data-testid="rfq-item-bulk-bar">
                <span className="text-xs uppercase tracking-widest font-bold">
                  {selectedItemIdx.size} kalem seçildi
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={clearItemSel}
                          data-testid="rfq-item-bulk-clear"
                          className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">
                    Seçimi Temizle
                  </Button>
                  <Button size="sm" onClick={bulkRemoveItems}
                          data-testid="rfq-item-bulk-delete"
                          className="rounded-none sm:rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs uppercase tracking-widest font-bold">
                    <TrashSimple size={14} className="mr-1.5" />
                    Seçilenleri Sil
                  </Button>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 pl-2 font-bold w-8">
                      <Checkbox
                        checked={selectedItemIdx.size > 0 && selectedItemIdx.size === form.items.length
                          ? true : (selectedItemIdx.size > 0 ? "indeterminate" : false)}
                        onCheckedChange={toggleAllItems}
                        data-testid="rfq-item-select-all"
                        aria-label="Tümünü seç"
                      />
                    </th>
                    <th className="text-left py-2 pl-2 font-bold w-10">No</th>
                    <th className="text-left py-2 px-2 font-bold">Malzemenin/Ürünün Açıklaması</th>
                    <th className="text-left py-2 px-2 font-bold w-28">Birim</th>
                    <th className="text-right py-2 px-2 font-bold w-32">Miktar</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => (
                    <tr key={i}
                        data-testid={`rfq-item-${i}`}
                        className={`border-b border-slate-100 ${selectedItemIdx.has(i) ? "bg-[#F7FBE9]/60" : ""}`}>
                      <td className="py-1.5 pl-2">
                        <Checkbox
                          checked={selectedItemIdx.has(i)}
                          onCheckedChange={() => toggleItemSel(i)}
                          data-testid={`rfq-item-check-${i}`}
                        />
                      </td>
                      <td className="py-1.5 pl-2 text-slate-400 font-mono text-xs">{i + 1}</td>
                      <td className="py-1.5 px-2">
                        <Input value={it.material_name} onChange={e => updateItem(i, { material_name: e.target.value })}
                               placeholder="Örn: Akrilik Esaslı Dış Cephe Boyası"
                               className="rounded-none sm:rounded-sm h-9" />
                      </td>
                      <td className="py-1.5 px-2">
                        <Select value={it.unit} onValueChange={(v) => updateItem(i, { unit: v })}>
                          <SelectTrigger className="rounded-none sm:rounded-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-1.5 px-2">
                        <Input type="number" step="0.01" value={it.quantity}
                               onChange={e => updateItem(i, { quantity: e.target.value })}
                               placeholder="0"
                               className="rounded-none sm:rounded-sm text-right font-mono h-9" />
                      </td>
                      <td className="py-1.5 px-1 text-right">
                        <button onClick={() => removeItem(i)} className="w-8 h-8 flex items-center justify-center hover:bg-red-50 text-red-600">
                          <TrashSimple size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      {/* Alıcı Firmalar */}
      <Section
        icon={Users}
        title="Teklif İstenen Firmalar"
        subtitle={`${form.recipients.length} firma`}
        actions={
          <Button size="sm" variant="outline" onClick={addRecipient} data-testid="rfq-add-recipient" className="rounded-none sm:rounded-sm">
            <Plus size={14} className="mr-1.5" /> Firma Ekle
          </Button>
        }
      >
        {form.recipients.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500 border border-dashed border-slate-300">
            Henüz firma yok. Talep gönderilecek firmaları ekleyin.
          </div>
        ) : (
          <div className="space-y-3">
            {form.recipients.map((r, i) => (
              <div key={i} className="p-4 border border-slate-200 bg-slate-50/50 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid={`rfq-recipient-${i}`}>
                <div className="md:col-span-1 flex items-center">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">#{i + 1}</span>
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Firma</Label>
                  <div className="mt-1 flex items-stretch gap-1.5">
                    <div className="flex-1 min-w-0">
                      <SupplierAutocomplete
                        value={r.supplier_id}
                        suppliers={suppliers}
                        onChange={(s) => updateRecipient(i, {
                          supplier_id: s?.id || "",
                          supplier_name: s?.name || "",
                        })}
                        placeholder="Firma adı yazın"
                        testId={`rfq-recipient-${i}-supplier`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => openSupplierSheetForRecipient(i)}
                      data-testid={`rfq-recipient-${i}-new-supplier`}
                      className="rounded-none sm:rounded-sm border-[#95C11F] text-[#4E7A0F] hover:bg-[#95C11F] hover:text-white h-9 px-2 shrink-0"
                      title="Sistemde yoksa yeni firma tanımla"
                    >
                      <Plus size={14} />
                    </Button>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">İlgili Personel</Label>
                  <Input value={r.contact_person} onChange={e => updateRecipient(i, { contact_person: e.target.value })}
                         placeholder="İsim" className="mt-1 rounded-none sm:rounded-sm h-9" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Talep Aracı</Label>
                  <Select value={r.channel} onValueChange={(v) => updateRecipient(i, { channel: v })}>
                    <SelectTrigger className="mt-1 rounded-none sm:rounded-sm h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Fiyat Teklifi</Label>
                  <Select value={r.response} onValueChange={(v) => updateRecipient(i, { response: v })}>
                    <SelectTrigger className="mt-1 rounded-none sm:rounded-sm h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RESPONSES.map(x => <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Açıklama / Marka</Label>
                  <Input value={r.note} onChange={e => updateRecipient(i, { note: e.target.value })}
                         placeholder="Örn: Knauf Insulation"
                         className="mt-1 rounded-none sm:rounded-sm h-9" />
                </div>
                <div className="md:col-span-12 flex justify-end">
                  <button onClick={() => removeRecipient(i)}
                          className="text-[10px] uppercase tracking-widest font-bold text-red-600 hover:text-red-800 flex items-center gap-1">
                    <TrashSimple size={12} /> Firmayı Kaldır
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Teslimat + Notlar */}
      <Section icon={Truck} title="Teslimat & Notlar" subtitle="Formda görünecek bilgiler">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Teslimat Adresi</Label>
            <Textarea value={form.delivery_address} onChange={e => set("delivery_address", e.target.value)}
                      placeholder="Örn: Sazlıbosna TOKİ Projesi, Arnavutköy, İstanbul"
                      rows={2} data-testid="rfq-delivery-address"
                      className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">İlgili Kişi (Ad)</Label>
            <Input value={form.contact_person_name} onChange={e => set("contact_person_name", e.target.value)}
                   placeholder="Örn: Onur Selçuk" className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">İlgili Kişi (Tel)</Label>
            <Input value={form.contact_person_phone} onChange={e => set("contact_person_phone", e.target.value)}
                   placeholder="+90 5xx xxx xx xx" className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Teslimat Tarihi</Label>
            <Input value={form.delivery_date_text} onChange={e => set("delivery_date_text", e.target.value)}
                   className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Nakliye</Label>
            <Textarea value={form.shipping_note} onChange={e => set("shipping_note", e.target.value)}
                      rows={2} className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Ödeme Türü</Label>
            <Textarea value={form.payment_type_note} onChange={e => set("payment_type_note", e.target.value)}
                      rows={2} className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Vade Şekli</Label>
            <Textarea value={form.payment_terms_note} onChange={e => set("payment_terms_note", e.target.value)}
                      rows={2} className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">İlave Açıklamalar</Label>
            <Textarea value={form.additional_notes} onChange={e => set("additional_notes", e.target.value)}
                      rows={3} className="mt-2 rounded-none sm:rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Talep Sahibi (Kaşe/İmza)</Label>
            <Input value={form.requester_name} onChange={e => set("requester_name", e.target.value)}
                   placeholder="Adı Soyadı" className="mt-2 rounded-none sm:rounded-sm" />
          </div>
        </div>
      </Section>

      <ItemsImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImport={handleImport}
      />

      {/* Recipient satırından açılan Hızlı Firma Kaydı Sheet'i */}
      <SupplierQuickCreate
        open={supplierSheetOpen}
        onOpenChange={(o) => {
          setSupplierSheetOpen(o);
          if (!o) setSupplierSheetForRecipient(null);
        }}
        pendingInfo={supplierSheetPending}
        pendingRole="issuer"
        saving={supplierSaving}
        saveFn={saveSupplierFromRecipient}
      />
    </Shell>
  );
}
